import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from 'react-bootstrap';
import { LuUser, LuBriefcase, LuBuilding2, LuMail } from 'react-icons/lu';
import Logo from 'components/common/Logo';
import { submitScreening } from '../../../store/auth';

const ScreeningPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    organisation_type: '',
    organisation: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.organisation_type) {
      newErrors.organisation_type = 'Organisation type is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call the backend API using the auth.js function
      const result = await submitScreening({
        name: formData.name,
        designation: formData.designation,
        organisation_type: formData.organisation_type,
        organisation: formData.organisation,
        email: formData.email
      });
      //TODO: update above code to store dev width and height in screening info

      // console.log('Screening submitted successfully:', result);

      // Store in localStorage as backup (optional)
      localStorage.setItem('screeningData', JSON.stringify(formData));
      localStorage.setItem('hasCompletedScreening', 'true');

      // Success - navigate to home
      navigate('/');
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({
        submit: error.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px 14px 48px',
    borderRadius: '12px',
    border: '2px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const iconContainerStyle = {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#718096',
    pointerEvents: 'none'
  };

  return (
    <div style={{ 
      backgroundColor: '#f8fbff', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden',
      width: '100%',
    }}>

      {/* Navbar with Logo and Title */}
      <Navbar
        className="navbar-glass fs--1 navbar-top"
        style={{ 
          background: 'linear-gradient(to right, #18275f, #0b1e6bff, #001f9c, #296eff, #4d8cff, #6fa8ff)',
          padding: '12px 0'
        }}
      >
        <div className="container-fluid">
          <div className="d-flex align-items-center">
            <Logo at="navbar-top" width={15} id="topLogo" />
          </div>
        </div>
      </Navbar>

      {/* Main Content */}
      <div style={{ 
        flex: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        <div style={{ 
          width: '100%',
          maxWidth: '600px'
        }}>
          {/* Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: 'clamp(32px, 5vw, 48px)',
            boxShadow: '0 8px 24px rgba(44, 82, 130, 0.12)',
            border: '2px solid #e2e8f0'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{
                color: '#2c5282',
                fontWeight: '700',
                fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                marginBottom: '12px'
              }}>
                Welcome to CDAP
              </h2>
              <p style={{
                color: '#718096',
                fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
                lineHeight: '1.6',
                margin: '0'
              }}>
                Please provide your details to access the platform
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Name Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  color: '#2c5282',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.95rem'
                }}>
                  Name <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={iconContainerStyle}>
                    <LuUser size={20} />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    style={{
                      ...inputStyle,
                      borderColor: errors.name ? '#e53e3e' : '#e2e8f0'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3182ce';
                      e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                    }}
                    onBlur={(e) => {
                      if (!errors.name) {
                        e.target.style.borderColor = '#e2e8f0';
                      }
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                {errors.name && (
                  <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '6px', marginBottom: '0' }}>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Designation Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  color: '#2c5282',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.95rem'
                }}>
                  Designation
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={iconContainerStyle}>
                    <LuBriefcase size={20} />
                  </div>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="Enter your designation"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3182ce';
                      e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Organisation Type Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  color: '#2c5282',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.95rem'
                }}>
                  Organisation Type <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={iconContainerStyle}>
                    <LuBuilding2 size={20} />
                  </div>
                  <select
                    name="organisation_type"
                    value={formData.organisation_type}
                    onChange={handleChange}
                    style={{
                      ...inputStyle,
                      borderColor: errors.organisation_type ? '#e53e3e' : '#e2e8f0',
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23718096\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 16px center',
                      backgroundSize: '20px',
                      paddingRight: '48px',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3182ce';
                      e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                    }}
                    onBlur={(e) => {
                      if (!errors.organisation_type) {
                        e.target.style.borderColor = '#e2e8f0';
                      }
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select organisation type</option>
                    <option value="Government">Government</option>
                    <option value="Non-profit / Civil Society Organisation (CSO / NGO)">Non-profit / Civil Society Organisation (CSO / NGO)</option>
                    <option value="Academic Institution">Academic Institution</option>
                    <option value="Research Organisation / Think Tank">Research Organisation / Think Tank</option>
                    <option value="Student">Student</option>
                    <option value="Independent Researcher / Consultant">Independent Researcher / Consultant</option>
                    <option value="Consulting organisation">Consulting organisation</option>
                    <option value="Private Company / Corporate">Private Company / Corporate</option>
                    <option value="Media / Journalism Organisation">Media / Journalism Organisation</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {errors.organisation_type && (
                  <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '6px', marginBottom: '0' }}>
                    {errors.organisation_type}
                  </p>
                )}
              </div>

              {/* Organisation Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  color: '#2c5282',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.95rem'
                }}>
                  Organisation
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={iconContainerStyle}>
                    <LuBuilding2 size={20} />
                  </div>
                  <input
                    type="text"
                    name="organisation"
                    value={formData.organisation}
                    onChange={handleChange}
                    placeholder="Enter your organisation"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3182ce';
                      e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Email Field */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  color: '#2c5282',
                  fontWeight: '600',
                  marginBottom: '8px',
                  fontSize: '0.95rem'
                }}>
                  Email ID <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <div style={iconContainerStyle}>
                    <LuMail size={20} />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    style={{
                      ...inputStyle,
                      borderColor: errors.email ? '#e53e3e' : '#e2e8f0'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3182ce';
                      e.target.style.boxShadow = '0 0 0 3px rgba(49, 130, 206, 0.1)';
                    }}
                    onBlur={(e) => {
                      if (!errors.email) {
                        e.target.style.borderColor = '#e2e8f0';
                      }
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                {errors.email && (
                  <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '6px', marginBottom: '0' }}>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: '#fff5f5', 
                  borderRadius: '8px',
                  border: '1px solid #feb2b2'
                }}>
                  <p style={{ color: '#e53e3e', fontSize: '0.875rem', margin: '0' }}>
                    {errors.submit}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: isSubmitting 
                    ? 'linear-gradient(135deg, #718096 0%, #a0aec0 100%)'
                    : 'linear-gradient(135deg, #488ce6ff 0%, #3182ce 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1.05rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(49, 130, 206, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(49, 130, 206, 0.3)';
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Continue'}
              </button>

              {/* Privacy Note */}
              <p style={{
                textAlign: 'center',
                color: '#718096',
                fontSize: '0.85rem',
                marginTop: '20px',
                marginBottom: '0',
                lineHeight: '1.5'
              }}>
                This platform is a prototype and is currently under active development. Features, data, and functionality may change, and certain components may operate in a limited or preliminary form.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreeningPage;